import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Bot, RefreshCw, Printer } from 'lucide-react';
import scheduleService from '../../services/scheduleService';

function AiScheduleReviewDialog({
  open,
  onClose,
  schedules,
  unassignedOrders,
  workCenters,
  dateRange,
}) {
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  // Depend on the primitive date strings, not the dateRange object: the parent
  // passes an inline object (new reference every render, e.g. on window resize),
  // which would otherwise retrigger the auto-run effect on every parent render.
  const rangeStart = dateRange?.start;
  const rangeEnd = dateRange?.end;

  const runReview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResponse('');

    try {
      const result = await scheduleService.aiReview({
        schedules: schedules || [],
        unassigned_orders: unassignedOrders || [],
        work_centers: workCenters || [],
        date_range: { start: rangeStart, end: rangeEnd },
      });

      if (!result?.success) {
        throw new Error(result?.error || 'AI 分析失败');
      }

      setResponse(result?.suggestions || '未返回建议内容');
    } catch (err) {
      console.error('AI schedule review failed:', err);
      setError(err?.response?.data?.error || err?.message || 'AI 分析失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [schedules, unassignedOrders, workCenters, rangeStart, rangeEnd]);

  // Auto-run when opened
  useEffect(() => {
    if (open) {
      runReview();
    }
  }, [open, runReview]);

  const handlePrint = useCallback(() => {
    if (!contentRef.current || !response) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('无法打开打印窗口，请检查浏览器是否拦截了弹窗');
      return;
    }

    const title = `AI 排产优化建议 - ${dateRange?.start || ''} 至 ${dateRange?.end || ''}`;
    const html = contentRef.current.innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${title}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              padding: 24px;
              margin: 0;
            }
            h1 { font-size: 20px; margin-bottom: 16px; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
            h2 { font-size: 17px; margin-top: 20px; margin-bottom: 10px; }
            h3 { font-size: 15px; margin-top: 16px; margin-bottom: 8px; }
            p { margin: 0 0 10px 0; }
            ul, ol { margin: 0 0 10px 20px; padding: 0; }
            li { margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 13px; }
            th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: 600; }
            tr:nth-child(even) { background-color: #fafafa; }
            code { background-color: #f0f0f0; padding: 2px 6px; border-radius: 3px; font-family: monospace; }
            pre { background-color: #f5f5f5; padding: 12px; border-radius: 4px; overflow-x: auto; }
            blockquote { border-left: 3px solid #ccc; margin: 0 0 10px 0; padding-left: 12px; color: #666; }
            hr { border: 0; border-top: 1px solid #ddd; margin: 16px 0; }
            strong { font-weight: 600; }
            .print-header { margin-bottom: 20px; }
            .print-header h1 { margin: 0; }
            .print-meta { color: #666; font-size: 12px; margin-top: 4px; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>AI 排产优化建议</h1>
            <div class="print-meta">日期范围：${dateRange?.start || ''} 至 ${dateRange?.end || ''}</div>
          </div>
          <div class="markdown-response">${html}</div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 200);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [response, dateRange]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            AI 排产优化建议
          </DialogTitle>
          <DialogDescription>
            基于当前日期范围内的已排产任务和待排订单，由 Qwen AI 给出降本增效建议。
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 px-6 py-2">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p>AI 正在分析排产数据，请稍候…</p>
            </div>
          )}

          {error && !loading && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && response && (
            <ScrollArea className="h-[50vh] rounded-md border bg-muted/40 p-4">
              <div ref={contentRef} className="markdown-response text-sm leading-relaxed text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {response}
                </ReactMarkdown>
              </div>
            </ScrollArea>
          )}

          {!loading && !error && !response && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              暂无分析结果
            </div>
          )}
        </div>

        <DialogFooter className="px-6 pb-6 pt-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runReview}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            重新分析
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={loading || !response}
          >
            <Printer className="h-4 w-4 mr-1" />
            打印
          </Button>
          <Button variant="default" size="sm" onClick={onClose} disabled={loading}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AiScheduleReviewDialog;
